import React from "react"
import styled from "styled-components"

import assertNever from "assert-never"

import {
  EditorStateUnion,
  Exchange,
  ExchangeChannel,
  TradingPair
} from "../../reducer/common.types"
import { FuturesInterval } from "../../reducer/futures.types"
import {
  HistoricalAPIYear,
  HistoricalPricesPeriod
} from "../../reducer/historical.types"
import {
  Day,
  DefaultNbaRequestsForChannel,
  Month,
  NBARequest,
  NbaSeason,
  NbaTeam,
  NbaYear,
  Retrieve
} from "../../reducer/nba.types"
import {
  DefaultNflRequestsForChannel,
  NFLRequest,
  NflSeasonPhase,
  NflStatType,
  NflTeam,
  NflWeek,
  NflYear
} from "../../reducer/nfl.types"
import { SbDispatch } from "../../reducer/reducer"
import { DropDown } from "./drop.down.field"
import { NumberTextField } from "./number.text.field"
import { StringTextField } from "./string.text.field"

interface Props {
  /**
   * We don't want the editor to be activated if we've selected a
   * socket API and we're not connected
   */
  editable: boolean
  state: EditorStateUnion
  dispatch: SbDispatch
}

const DropDownsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  margin-bottom: 1em;

  /* Space out the children  */
  & > * {
    margin-right: 1em;
  }
`

interface Args {
  state: EditorStateUnion
  editable: boolean
  dispatch: SbDispatch
}

function getDropDowns({ state, editable, dispatch }: Args): JSX.Element[] {
  switch (state.api) {
    case "NBA": {
      const alwaysVisible = (
        <DropDown<NBARequest>
          value={state.channel}
          editable={editable}
          onChange={channel =>
            dispatch({
              type: "SET_EDITOR_STATE",
              state: DefaultNbaRequestsForChannel[channel]
            })
          }
          alternatives={NBARequest.all}
          title="Channel"
          key="Channel"
        />
      )

      switch (state.channel) {
        case "games": {
          return [
            alwaysVisible,
            <DropDown
              title="Year"
              key="Year"
              alternatives={NbaYear.all}
              value={state.year}
              editable={editable}
              onChange={year =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, year }
                })
              }
            />,

            <DropDown
              title="Month"
              key="Month"
              alternatives={Month.all}
              value={state.month}
              editable={editable}
              onChange={month =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, month }
                })
              }
            />,

            <DropDown
              title="Day"
              key="Day"
              alternatives={Day.all}
              value={state.day}
              editable={editable}
              onChange={day =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, day }
                })
              }
            />,
            <DropDown
              title="Team"
              key="Team"
              alternatives={NbaTeam.all}
              value={state.teamId}
              editable={editable}
              onChange={teamId =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, teamId }
                })
              }
            />
          ]
        }
        case "players": {
          return [
            alwaysVisible,
            <StringTextField
              title="First name"
              key="First name"
              value={state.firstName}
              editable={editable}
              onChange={firstName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, firstName }
                })
              }
            />,
            <StringTextField
              title="Last name"
              key="Last name"
              value={state.lastName}
              editable={editable}
              onChange={lastName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, lastName }
                })
              }
            />
          ]
        }
        case "stats": {
          return [
            alwaysVisible,
            <StringTextField
              title="First name"
              key="First name"
              value={state.firstName}
              editable={editable}
              onChange={firstName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, firstName }
                })
              }
            />,
            <StringTextField
              title="Last name"
              key="Last name"
              value={state.lastName}
              editable={editable}
              onChange={lastName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, lastName }
                })
              }
            />,
            <DropDown
              title="Year"
              key="Year"
              alternatives={NbaYear.all}
              value={state.year}
              editable={editable}
              onChange={year =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, year }
                })
              }
            />,
            <DropDown
              title="Month"
              key="Month"
              alternatives={Month.all}
              value={state.month}
              editable={editable}
              onChange={month =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, month }
                })
              }
            />,
            <DropDown
              title="Day"
              key="Day"
              alternatives={Day.all}
              value={state.day}
              editable={editable}
              onChange={day =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, day }
                })
              }
            />
          ]
        }
        case "team": {
          return [
            alwaysVisible,
            <DropDown
              title="Team"
              key="Team"
              alternatives={NbaTeam.all}
              value={state.teamId}
              editable={editable}
              onChange={teamId =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, teamId }
                })
              }
            />,
            <DropDown
              title="Retrieve"
              key="Retrieve"
              alternatives={Retrieve.all}
              value={state.retrieve}
              editable={editable}
              onChange={retrieve =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, retrieve }
                })
              }
            />,
            <DropDown
              title="Season"
              key="Season"
              alternatives={NbaSeason.all}
              value={state.season}
              editable={editable}
              onChange={season =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, season }
                })
              }
            />
          ]
        }
        case "info": {
          return [alwaysVisible]
        }
        default:
          return assertNever(state)
      }
    }
    case "NFL": {
      const alwaysVisible = (
        <DropDown<NFLRequest>
          value={state.channel}
          editable={editable}
          onChange={channel =>
            dispatch({
              type: "SET_EDITOR_STATE",
              state: DefaultNflRequestsForChannel[channel]
            })
          }
          alternatives={NFLRequest.all}
          title="Channel"
          key="Channel"
        />
      )

      switch (state.channel) {
        case "games": {
          return [
            alwaysVisible,
            <DropDown
              title="Week"
              key="Week"
              alternatives={NflWeek.all}
              value={state.week}
              editable={editable}
              onChange={week =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, week }
                })
              }
            />,
            <DropDown
              title="Year"
              key="Year"
              alternatives={NflYear.all}
              value={state.year}
              editable={editable}
              onChange={year =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, year }
                })
              }
            />,

            <DropDown
              title="Season phase"
              key="Season phase"
              alternatives={NflSeasonPhase.all}
              value={state.seasonPhase}
              editable={editable}
              onChange={seasonPhase =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, seasonPhase }
                })
              }
            />,
            <DropDown
              title="Team"
              key="Team"
              alternatives={NflTeam.all}
              value={state.teamId}
              editable={editable}
              onChange={teamId =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, teamId }
                })
              }
            />
          ]
        }
        case "players": {
          return [
            alwaysVisible,
            <StringTextField
              title="First name"
              key="First name"
              value={state.firstName}
              editable={editable}
              onChange={firstName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, firstName }
                })
              }
            />,
            <StringTextField
              title="Last name"
              key="Last name"
              value={state.lastName}
              editable={editable}
              onChange={lastName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, lastName }
                })
              }
            />
          ]
        }
        case "stats": {
          return [
            alwaysVisible,
            <StringTextField
              title="First name"
              key="First name"
              value={state.firstName}
              editable={editable}
              onChange={firstName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, firstName }
                })
              }
            />,
            <StringTextField
              title="Last name"
              key="Last name"
              value={state.lastName}
              editable={editable}
              onChange={lastName =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, lastName }
                })
              }
            />,
            <DropDown
              title="Season Phase"
              key="Season Phase"
              alternatives={NflSeasonPhase.all}
              value={state.seasonPhase}
              editable={editable}
              onChange={seasonPhase =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, seasonPhase }
                })
              }
            />,
            <DropDown
              title="Year"
              key="Year"
              alternatives={NflYear.all}
              value={state.year}
              editable={editable}
              onChange={year =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, year }
                })
              }
            />,
            <DropDown
              title="Week"
              key="Week"
              alternatives={NflWeek.all}
              value={state.week}
              editable={editable}
              onChange={week =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, week }
                })
              }
            />,

            <DropDown
              title="Stat type"
              key="Stat type"
              alternatives={NflStatType.all}
              value={state.statType}
              editable={editable}
              onChange={statType =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, statType }
                })
              }
            />
          ]
        }
        case "team": {
          return [
            alwaysVisible,
            <DropDown
              title="Team"
              key="Team"
              alternatives={NflTeam.all}
              value={state.teamId}
              editable={editable}
              onChange={teamId =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, teamId }
                })
              }
            />,
            <DropDown
              title="Retrieve"
              key="Retrieve"
              alternatives={Retrieve.all}
              value={state.retrieve}
              editable={editable}
              onChange={retrieve =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, retrieve }
                })
              }
            />,
            <DropDown
              title="Year"
              key="Year"
              alternatives={NflYear.all}
              value={state.year}
              editable={editable}
              onChange={year =>
                dispatch({
                  type: "SET_EDITOR_STATE",
                  state: { ...state, year }
                })
              }
            />
          ]
        }
        case "info": {
          return [alwaysVisible]
        }
        default:
          return assertNever(state)
      }
    }
    case "futures": {
      return [
        <DropDown
          value={state.exchange}
          editable={editable}
          onChange={exchange => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, exchange }
            })
          }}
          title="Exchange"
          key="Exchange"
          alternatives={Exchange.all}
        />,
        <DropDown
          editable={editable}
          value={state.symbol}
          title="Pair"
          key="Pair"
          alternatives={TradingPair.all}
          onChange={symbol =>
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, symbol }
            })
          }
        />,
        <DropDown
          value={state.channel}
          editable={editable}
          onChange={channel => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, channel }
            })
          }}
          title="Channel"
          key="Channel"
          alternatives={ExchangeChannel.all}
        />,
        <DropDown
          value={state.interval}
          editable={editable}
          onChange={interval => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, interval }
            })
          }}
          title="Interval"
          key="Interval"
          alternatives={FuturesInterval.all}
        />,
        <NumberTextField
          value={state.duration}
          editable={editable}
          onChange={duration => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: {
                ...state,
                duration
              }
            })
          }}
          title="Duration"
          key="Duration"
        />
      ]
    }
    case "spot": {
      return [
        <DropDown
          value={state.exchange}
          editable={editable}
          onChange={exchange => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, exchange }
            })
          }}
          title="Exchange"
          key="Exchange"
          alternatives={Exchange.all}
        />,
        <DropDown
          value={state.symbol}
          editable={editable}
          title="Pair"
          key="Pair"
          alternatives={TradingPair.all}
          onChange={symbol =>
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, symbol }
            })
          }
        />,
        <DropDown
          value={state.channel}
          editable={editable}
          onChange={channel => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, channel }
            })
          }}
          title="Channel"
          key="Channel"
          alternatives={ExchangeChannel.all}
        />,
        <NumberTextField
          editable={editable}
          value={state.duration}
          onChange={duration => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: {
                ...state,
                duration
              }
            })
          }}
          title="Duration"
          key="Duration"
        />
      ]
    }
    case "historical": {
      return [
        <DropDown
          value={state.exchange}
          editable={editable}
          onChange={exchange => {
            dispatch({
              type: "SET_EDITOR_STATE",
              state: { ...state, exchange }
            })
          }}
          title="Exchange"
          key="Exchange"
          alternatives={Exchange.all}
        />,
        <DropDown
          editable={editable}
          value={state.symbol}
          title="Pair"
          key="Pair"
          alternatives={TradingPair.all}
          onChange={symbol =>
            dispatch({ type: "SET_EDITOR_STATE", state: { ...state, symbol } })
          }
        />,
        <DropDown
          editable={editable}
          value={state.year}
          onChange={year =>
            dispatch({ type: "SET_EDITOR_STATE", state: { ...state, year } })
          }
          title="Year"
          key="Year"
          alternatives={HistoricalAPIYear.all}
        />,
        <DropDown
          editable={editable}
          value={state.period}
          onChange={period =>
            dispatch({ type: "SET_EDITOR_STATE", state: { ...state, period } })
          }
          title="Period"
          key="Period"
          alternatives={HistoricalPricesPeriod.all}
        />
      ]
    }
    default: {
      return assertNever(state)
    }
  }
}

export const MessageEditorDropdowns: React.FunctionComponent<Props> = ({
  state,
  editable,
  dispatch
}) => {
  return (
    <DropDownsContainer>
      {getDropDowns({ state, dispatch, editable })}
    </DropDownsContainer>
  )
}
